/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IAssistanceLog,
  IAssistanceLogList,
  IKnowledgeArticle,
  IKnowledgeAskResponse,
  IKnowledgeIngestOperation,
} from "../types/knowledgeArticle";
import backendApi from "./backendApi";
import { showSnackbar } from "./snackbar.service";

const routePrefix = "datalake/knowledge";

export default {
  async getAll() {
    try {
      const result = await backendApi()?.get(`${routePrefix}`);
      return result.data as IKnowledgeArticle[];
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load knowledge articles."
        );
        throw error;
      }
    }
  },
  async create(article: IKnowledgeArticle) {
    try {
      const result = await backendApi()?.post(`${routePrefix}`, article);
      return result.data as IKnowledgeArticle;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not add the article."
        );
        throw error;
      }
    }
  },
  async update(articleId: string, article: IKnowledgeArticle) {
    try {
      const result = await backendApi()?.put(
        `${routePrefix}/${articleId}`,
        article
      );
      return result.data as IKnowledgeArticle;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not save the article."
        );
        throw error;
      }
    }
  },
  async delete(articleId: string) {
    try {
      const result = await backendApi()?.delete(`${routePrefix}/${articleId}`);
      return result.data as IKnowledgeArticle;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not delete the article."
        );
        throw error;
      }
    }
  },
  async getLogs(params?: { page?: number; pageSize?: number; search?: string }) {
    try {
      const result = await backendApi()?.get(`${routePrefix}/logs`, {
        params: {
          page: params?.page || 1,
          pageSize: params?.pageSize || 30,
          search: params?.search || "",
        },
      });
      return result.data as IAssistanceLogList;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load ask logs."
        );
        throw error;
      }
    }
  },
  async getUnreadAdminNoteCount() {
    try {
      const result = await backendApi()?.get(`${routePrefix}/logs/unread-count`);
      return (result.data?.count || 0) as number;
    } catch (error: any) {
      console.error(error);
      return 0;
    }
  },
  async markAdminNotesSeen(ids?: string[]) {
    try {
      await backendApi()?.post(`${routePrefix}/logs/mark-seen`, {
        ids: ids || [],
      });
    } catch (error: any) {
      console.error(error);
    }
  },
  async getMyLogs() {
    try {
      const result = await backendApi()?.get(`${routePrefix}/logs/mine`);
      return result.data as IAssistanceLog[];
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load your questions."
        );
        throw error;
      }
    }
  },
  async getLog(logId: string) {
    try {
      const result = await backendApi()?.get(`${routePrefix}/logs/${logId}`);
      return result.data as IAssistanceLog;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not load that question."
        );
        throw error;
      }
    }
  },
  async reviewLog(logId: string, additionalAnswer: string) {
    try {
      const result = await backendApi()?.post(
        `${routePrefix}/logs/${logId}/review`,
        { additionalAnswer }
      );
      return result.data as IAssistanceLog;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not save the additional answer."
        );
        throw error;
      }
    }
  },
  async ask(
    question: string,
    currentPath?: string,
    history?: { question: string; answer: string }[],
    parentLogId?: string
  ) {
    try {
      const result = await backendApi()?.post(`${routePrefix}/ask`, {
        question,
        currentPath,
        history,
        parentLogId,
      });
      return result.data as IKnowledgeAskResponse;
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not answer that question."
        );
        throw error;
      }
    }
  },
  async ingestPreview(sourceText: string) {
    try {
      const result = await backendApi()?.post(`${routePrefix}/ingest/preview`, {
        sourceText,
      });
      return result.data as { operations: IKnowledgeIngestOperation[] };
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not preview the knowledge changes."
        );
        throw error;
      }
    }
  },
  async ingestApply(operations: IKnowledgeIngestOperation[]) {
    try {
      const result = await backendApi()?.post(`${routePrefix}/ingest/apply`, {
        operations,
      });
      return result.data as {
        created: IKnowledgeArticle[];
        updated: IKnowledgeArticle[];
      };
    } catch (error: any) {
      if (error?.response?.status) {
        showSnackbar(
          "danger",
          "Error",
          error?.response?.data?.message || "Could not update the knowledge pool."
        );
        throw error;
      }
    }
  },
};
